/**
 * InlinePanelFilters – Domain Component (Layer 2)
 *
 * An inline, scrollable advanced-filter form designed to be embedded
 * inside the HybridPageChatPanel as a toggle-able alternative to the
 * Intel chat view.  It reuses the same atomic controls as the
 * SavedSearchFilters modal (ButtonGroup, Toggle, TextField,
 * FilterDropdownPropertyType) but manages its own local filter state
 * and exposes "Reset" / "Apply" actions.
 *
 * Props mirror the filter-related subset that `HybridPageChatPanel`
 * already receives so the parent can simply forward them.
 */

import React, { useCallback, useState } from 'react';
import { buildClassName } from 'utils/build-class-name';

import Filter from 'components/modal-dialog/saved-search-filters/filter';
import ButtonGroup from 'components/control/button-group';
import Toggle from 'components/control/toggle';
import TextField from 'components/control/text-field';
import FilterDropdownPropertyType from 'components/search-bar/advanced-search/filter-dropdown/home-type';
import Button from 'components/control/button';
import { NumberDropdownOptionList } from 'components/search-bar/advanced-search/filter-dropdown/number-range';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';

import GarageIcon from 'components/icon/filters/garage-icon';
import WaterfrontIcon from 'components/icon/filters/waterfront-icon';
import { NoImageIcon, OpenHouseIcon, PoolIcon } from 'components/icon/filters';
import FireplaceIcon from 'components/icon/filters/fireplace-icon';

import {
  defaultRentPriceOptions,
  defaultBuyPriceOptions,
  defaultSqftOptions,
  propertyTypeOptions,
} from 'utils/select-options';
import { getLocalizedBedroomBathroomParkingOptions } from 'utils/localized-select-options';
import { parameterContainsIsMore } from 'utils/listing-query-helper';
import { isStringNumeric } from 'utils/format-number';
import { formatNumber } from '@zoocasa/node-kit/numbers';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useTranslation } from 'next-i18next';
import { LOCALE_MODAL_PATHS, Namespaces } from 'types/locales';
import defaultListingParams from 'contexts/preferences/listing-params/defaults';
import { DEFAULT_HAS_IMAGE_VALUE } from 'contexts/preferences/listing-params';

import styles from './inline-panel-filters.module.scss';

import type { Filter as ListingParamsFilter } from 'contexts/preferences/listing-params/types';
import type { ListingFilterBarType } from 'components/listing-filter-bar/listing_filter_bar';
import type { PartialDeep } from 'type-fest';

// ─── Props ───

export interface InlinePanelFiltersProps {
  /** Current search filters (used to initialise local state). */
  filters: ListingFilterBarType;
  /** Called with the full local filter state when the user taps "Apply Filters". */
  onApply: (filters: PartialDeep<ListingParamsFilter>, changedFilter?: string) => void;
  /** Optional extra className on the root element. */
  className?: string;
}

// ─── Constants ───

const MIN = 'min' as const;
const MAX = 'max' as const;

// ─── Component ───

export function InlinePanelFilters({ filters: externalFilters, onApply, className }: InlinePanelFiltersProps) {
  // ── Local state (mirrors modal pattern) ──
  const [filters, setFilters] = useState<ListingFilterBarType>({ ...externalFilters });
  const { parseCurrency } = useI18nFormatting();
  const { t } = useTranslation(Namespaces.COMMON);

  // Price dropdown state
  const [activePriceType, setActivePriceType] = useState('');
  const [activeSqftType, setActiveSqftType] = useState('');

  // Bed exact-number toggle
  const [isExactBedroomNumber] = useState(!parameterContainsIsMore(filters.bedrooms));
  const bedroomNumberOptions = getLocalizedBedroomBathroomParkingOptions(t, isExactBedroomNumber);
  const bathroomAndParkingOptions = getLocalizedBedroomBathroomParkingOptions(t, false);

  const priceOptions = filters.rental ? defaultRentPriceOptions : defaultBuyPriceOptions;

  // ── Helpers ──

  const updateFilters = useCallback((newFilters: ListingFilterBarType) => {
    setFilters({ ...newFilters, hasImage: filters.hasImage !== undefined ? filters.hasImage : DEFAULT_HAS_IMAGE_VALUE });
  }, [filters.hasImage]);

  const resetFilters = useCallback(() => {
    setFilters({
      ...filters,
      ...defaultListingParams['filter'],
      hasImage: DEFAULT_HAS_IMAGE_VALUE,
    });
  }, [filters]);

  const handleApply = useCallback(() => {
    // Validate ranges before applying
    const validated = { ...filters };
    if (validated.priceMin && validated.priceMax && validated.priceMin > validated.priceMax) {
      validated.priceMax = null;
    }
    if (validated.sqftMin && validated.sqftMax && validated.sqftMin > validated.sqftMax) {
      validated.sqftMax = null;
    }
    onApply(validated as PartialDeep<ListingParamsFilter>, 'inline-filters');
  }, [filters, onApply]);

  // ── Render ──

  return (
    <div className={buildClassName(styles['root'], className)}>
      {/* Scrollable filter area */}
      <div className={styles['scrollable']}>
        {/* ── Home Type ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_SECTIONS_HOME_TYPE, { defaultValue: 'Home Type' })}
        >
          <FilterDropdownPropertyType
            checkboxNamePrefix="inline-panel-filters"
            currentHomeTypeFilter={filters.homeType}
            onValueChange={value => updateFilters({ ...filters, homeType: value })}
            isInDropdown={false}
            propertyTypeList={propertyTypeOptions}
          />
        </Filter>

        {/* ── Price ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_SECTIONS_PRICE, { defaultValue: 'Price' })}
        >
          <div className={styles['range-row']} ref={useOutsideClickHandler(() => setActivePriceType(''))}>
            <div className={styles['range-field']}>
              <TextField
                placeholder={t(LOCALE_MODAL_PATHS.FILTER_PLACEHOLDERS_NO_MIN, { defaultValue: 'No Min' })}
                isCurrency
                value={filters.priceMin ? formatNumber(filters.priceMin) : ''}
                onValueChange={value => {
                  if (value === '0' || value === '') {
                    setFilters({ ...filters, priceMin: null });
                  } else {
                    const parsed = parseCurrency(value);
                    if (parsed !== null && isStringNumeric(parsed.toString())) {
                      setFilters({ ...filters, priceMin: parsed });
                    }
                  }
                }}
                onClick={() => setActivePriceType(MIN)}
              />
              <NumberDropdownOptionList
                isActive={activePriceType === MIN}
                isCurrency
                options={priceOptions}
                setPrice={value => setFilters({ ...filters, priceMin: parseCurrency(value) })}
                type={MIN}
                setActiveType={setActivePriceType}
                value={filters.priceMin ? formatNumber(filters.priceMin) : ''}
                maxValue={filters.priceMax ? formatNumber(filters.priceMax) : ''}
              />
            </div>
            <span className={styles['range-divider']}>–</span>
            <div className={styles['range-field']}>
              <TextField
                placeholder={t(LOCALE_MODAL_PATHS.FILTER_PLACEHOLDERS_NO_MAX, { defaultValue: 'No Max' })}
                isCurrency
                value={filters.priceMax ? formatNumber(filters.priceMax) : ''}
                onValueChange={value => {
                  if (value === '0' || value === '') {
                    setFilters({ ...filters, priceMax: null });
                  } else {
                    const parsed = parseCurrency(value);
                    if (parsed !== null && isStringNumeric(parsed.toString())) {
                      setFilters({ ...filters, priceMax: parsed });
                    }
                  }
                }}
                onClick={() => setActivePriceType(MAX)}
              />
              <NumberDropdownOptionList
                isActive={activePriceType === MAX}
                isCurrency
                options={priceOptions}
                setPrice={value => setFilters({ ...filters, priceMax: parseCurrency(value) })}
                type={MAX}
                setActiveType={setActivePriceType}
                value={filters.priceMax ? formatNumber(filters.priceMax) : ''}
                minValue={filters.priceMin ? formatNumber(filters.priceMin) : ''}
              />
            </div>
          </div>
        </Filter>

        {/* ── Beds ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_SECTIONS_BEDS, { defaultValue: 'Beds' })}
        >
          <ButtonGroup
            options={bedroomNumberOptions}
            value={filters.bedrooms}
            onValueChange={value => setFilters({ ...filters, bedrooms: value })}
            size="small"
          />
        </Filter>

        {/* ── Bathrooms ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_SECTIONS_BATHROOMS, { defaultValue: 'Bathrooms' })}
        >
          <ButtonGroup
            options={bathroomAndParkingOptions}
            value={filters.bathrooms}
            onValueChange={value => setFilters({ ...filters, bathrooms: value })}
            size="small"
          />
        </Filter>

        {/* ── Parking ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_SECTIONS_PARKING, { defaultValue: 'Parking' })}
        >
          <ButtonGroup
            options={bathroomAndParkingOptions}
            value={filters.parkingSpaces}
            onValueChange={value => setFilters({ ...filters, parkingSpaces: value })}
            size="small"
          />
        </Filter>

        {/* ── Square Feet ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_SECTIONS_SQUARE_FEET, { defaultValue: 'Square Feet' })}
        >
          <div className={styles['range-row']} ref={useOutsideClickHandler(() => setActiveSqftType(''))}>
            <div className={styles['range-field']}>
              <TextField
                placeholder={t(LOCALE_MODAL_PATHS.FILTER_PLACEHOLDERS_NO_MIN, { defaultValue: 'No Min' })}
                value={filters.sqftMin ? formatNumber(filters.sqftMin) : ''}
                onValueChange={value => {
                  if (value === '' || value === '0') {
                    setFilters({ ...filters, sqftMin: null });
                  } else {
                    const parsed = parseCurrency(value);
                    if (parsed !== null && isStringNumeric(parsed.toString())) {
                      setFilters({ ...filters, sqftMin: parsed });
                    }
                  }
                }}
                onClick={() => setActiveSqftType(MIN)}
              />
              <NumberDropdownOptionList
                isActive={activeSqftType === MIN}
                isCurrency={false}
                options={defaultSqftOptions}
                setPrice={value => setFilters({ ...filters, sqftMin: parseCurrency(value) })}
                type={MIN}
                setActiveType={setActiveSqftType}
                value={filters.sqftMin ? formatNumber(filters.sqftMin) : ''}
                maxValue={filters.sqftMax ? formatNumber(filters.sqftMax) : ''}
              />
            </div>
            <span className={styles['range-divider']}>–</span>
            <div className={styles['range-field']}>
              <TextField
                placeholder={t(LOCALE_MODAL_PATHS.FILTER_PLACEHOLDERS_NO_MAX, { defaultValue: 'No Max' })}
                value={filters.sqftMax ? formatNumber(filters.sqftMax) : ''}
                onValueChange={value => {
                  if (value === '' || value === '0') {
                    setFilters({ ...filters, sqftMax: null });
                  } else {
                    const parsed = parseCurrency(value);
                    if (parsed !== null && isStringNumeric(parsed.toString())) {
                      setFilters({ ...filters, sqftMax: parsed });
                    }
                  }
                }}
                onClick={() => setActiveSqftType(MAX)}
              />
              <NumberDropdownOptionList
                isActive={activeSqftType === MAX}
                isCurrency={false}
                options={defaultSqftOptions}
                setPrice={value => setFilters({ ...filters, sqftMax: parseCurrency(value) })}
                type={MAX}
                setActiveType={setActiveSqftType}
                value={filters.sqftMax ? formatNumber(filters.sqftMax) : ''}
                minValue={filters.sqftMin ? formatNumber(filters.sqftMin) : ''}
              />
            </div>
          </div>
        </Filter>

        {/* ── Amenities ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_SECTIONS_AMENITIES, { defaultValue: 'Amenities' })}
        >
          <Toggle
            label={t(LOCALE_MODAL_PATHS.FILTER_AMENITY_LABELS_GARAGE, { defaultValue: 'Garage' })}
            value={filters.garage}
            onClick={() => updateFilters({ ...filters, garage: !filters.garage })}
            isFullWidth
            Icon={GarageIcon}
          />
          <Toggle
            label={t(LOCALE_MODAL_PATHS.FILTER_AMENITY_LABELS_OPEN_HOUSE, { defaultValue: 'Open House' })}
            value={filters.openHouse}
            onClick={() => updateFilters({ ...filters, openHouse: !filters.openHouse })}
            isFullWidth
            Icon={OpenHouseIcon}
          />
          <Toggle
            label={t(LOCALE_MODAL_PATHS.FILTER_AMENITY_LABELS_POOL, { defaultValue: 'Pool' })}
            value={filters.pool}
            onClick={() => updateFilters({ ...filters, pool: !filters.pool })}
            isFullWidth
            Icon={PoolIcon}
          />
          <Toggle
            label={t(LOCALE_MODAL_PATHS.FILTER_AMENITY_LABELS_WATERFRONT, { defaultValue: 'Waterfront' })}
            value={filters.waterfront}
            onClick={() => updateFilters({ ...filters, waterfront: !filters.waterfront })}
            isFullWidth
            Icon={WaterfrontIcon}
          />
          <Toggle
            label={t(LOCALE_MODAL_PATHS.FILTER_AMENITY_LABELS_FIREPLACE, { defaultValue: 'Fireplace' })}
            value={filters.fireplace}
            onClick={() => updateFilters({ ...filters, fireplace: !filters.fireplace })}
            isFullWidth
            Icon={FireplaceIcon}
          />
        </Filter>

        {/* ── Hide listings without images ── */}
        <Filter
          label={t(LOCALE_MODAL_PATHS.FILTER_TOGGLES_HIDE_NO_IMAGES, { defaultValue: 'Listing Images' })}
        >
          <Toggle
            label={t(LOCALE_MODAL_PATHS.FILTER_TOGGLES_HIDE_NO_IMAGES, { defaultValue: 'Hide listings without images' })}
            value={filters.hasImage}
            onClick={() => setFilters(prev => ({ ...prev, hasImage: !prev.hasImage }))}
            isFullWidth
            Icon={NoImageIcon}
          />
        </Filter>
      </div>

      {/* ── Footer ── */}
      <div className={styles['footer']}>
        <Button
          label={t(LOCALE_MODAL_PATHS.FILTER_BUTTONS_RESET_FILTERS, { defaultValue: 'Reset Filters' })}
          type="button"
          theme="secondary"
          onClick={resetFilters}
          className={styles['reset-button']}
        />
        <Button
          label={t(LOCALE_MODAL_PATHS.FILTER_BUTTONS_APPLY_FILTERS, { defaultValue: 'Apply Filters' })}
          type="button"
          theme="primary"
          onClick={handleApply}
          className={styles['apply-button']}
        />
      </div>
    </div>
  );
}

export default InlinePanelFilters;
